import { useState } from 'react';
import type { Cluster, ClusterThresholds, CorrelationResult, CorrelationWindow } from '@prm/shared';
import { num, pct } from '../format';
import { Panel } from './Panel';
import { FlagList } from './FlagList';

const POS = 'var(--corr-pos)';
const NEG = 'var(--corr-neg)';

/**
 * Diverging scale: warm for names that move together (the risk this card exists
 * to surface), cool for genuine offsets, fading to the bare panel through the
 * uncorrelated middle so clusters read as blocks without every cell shouting.
 *
 * Colour is never the only encoding — every cell also prints its value, so the
 * matrix is fully readable in greyscale or with any colour vision deficiency.
 */
function cellStyle(v: number | null): { background: string; color: string } {
  if (v === null) return { background: 'transparent', color: 'var(--faint)' };
  const a = Math.min(1, Math.abs(v));
  const alpha = (a * 0.82).toFixed(3);
  // Flip the ink once the fill is dark enough to swallow the default text colour.
  if (v >= 0) return { background: `rgba(${POS}, ${alpha})`, color: a > 0.55 ? '#fff' : 'var(--text)' };
  return { background: `rgba(${NEG}, ${alpha})`, color: a > 0.55 ? '#04222b' : 'var(--text)' };
}

interface Props {
  correlation: CorrelationResult;
  thresholds: ClusterThresholds;
}

export function CorrelationCard({ correlation, thresholds }: Props) {
  const [window, setWindow] = useState<CorrelationWindow>(correlation.primaryWindow);
  const matrix = correlation.matrices.find((m) => m.window === window) ?? correlation.matrices[0];
  const { tickers } = correlation;

  return (
    <>
      <Panel
        title="Correlation"
        sub={`${window}d window · ${tickers.length} names`}
        actions={
          <>
            {correlation.matrices.map((m) => (
              <button
                key={m.window}
                className={`button button--sm ${m.window === window ? 'is-on' : ''}`}
                onClick={() => setWindow(m.window)}
              >
                {m.window}d
              </button>
            ))}
          </>
        }
        flush
      >
        <div className="panel__body" style={{ paddingBottom: 0 }}>
          <div className="kv">
            <Stat
              k="Effective N"
              v={correlation.effectiveN === null ? '—' : num(correlation.effectiveN, 1)}
              note="correlation-adjusted bets"
              bad={correlation.effectiveN !== null && correlation.effectiveN < 3}
            />
            <Stat
              k="Hard-only N"
              v={correlation.effectiveNHard === null ? '—' : num(correlation.effectiveNHard, 1)}
              note="hard clusters collapsed"
            />
            <Stat k="Naive N" v={num(correlation.naiveN, 1)} note="1 / HHI, pre-correlation" />
            <Stat
              k="Clusters"
              v={`${correlation.clusters.filter((c) => c.tier === 'hard').length} / ${correlation.softClusters.filter((c) => c.tier === 'soft').length}`}
              note="hard / soft"
            />
            <Stat
              k="Avg corr to SPY"
              v={correlation.avgSpyCorrelation === null ? '—' : num(correlation.avgSpyCorrelation)}
              note="gross-weighted"
            />
          </div>
        </div>

        {tickers.length < 2 || !matrix ? (
          <div className="empty">Need at least two positions with overlapping history to build a matrix.</div>
        ) : (
          <div className="heatmap__scroll">
            <table className="heatmap">
              <thead>
                <tr>
                  <th className="heatmap__corner" />
                  {tickers.map((t) => (
                    <th key={t} className="heatmap__colhead">
                      <span>{t}</span>
                    </th>
                  ))}
                  <th className="heatmap__spy">SPY</th>
                </tr>
              </thead>
              <tbody>
                {tickers.map((rowTicker, i) => (
                  <tr key={rowTicker}>
                    <th className="heatmap__rowhead">{rowTicker}</th>
                    {tickers.map((colTicker, j) => {
                      const v = matrix.values[i]?.[j] ?? null;
                      const obs = matrix.observations[i]?.[j] ?? 0;
                      return (
                        <td
                          key={colTicker}
                          className="heatmap__cell"
                          style={cellStyle(v)}
                          title={
                            v === null
                              ? `${rowTicker} / ${colTicker}: not measured (${obs} overlapping observations)`
                              : `${rowTicker} / ${colTicker}: ${v.toFixed(3)} over ${obs} observations`
                          }
                        >
                          {v === null ? '·' : v.toFixed(2).replace('0.', '.').replace('-.', '−.')}
                        </td>
                      );
                    })}
                    <td
                      className="heatmap__cell heatmap__spy"
                      style={cellStyle(correlation.spyCorrelation[rowTicker] ?? null)}
                      title={`${rowTicker} correlation to SPY over ${correlation.primaryWindow}d`}
                    >
                      {correlation.spyCorrelation[rowTicker] == null
                        ? '·'
                        : (correlation.spyCorrelation[rowTicker] as number).toFixed(2).replace('0.', '.').replace('-.', '−.')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="legend">
          <span className="legend__swatch" style={{ background: `rgba(${NEG}, 0.82)` }} />
          <span className="faint">−1 offsetting</span>
          <span className="legend__swatch" style={{ background: 'var(--surface-3)' }} />
          <span className="faint">0 independent</span>
          <span className="legend__swatch" style={{ background: `rgba(${POS}, 0.82)` }} />
          <span className="faint">+1 one bet</span>
          <span className="faint" style={{ marginLeft: 'auto' }}>
            · = too little overlapping history to measure
          </span>
        </div>
      </Panel>

      <Panel
        title="Clusters"
        sub={`hard ≥ ${num(thresholds.hard)} · soft ≥ ${num(thresholds.soft)} avg pairwise`}
        flush
      >
        {correlation.softClusters.length === 0 ? (
          <div className="empty">No clusters detected.</div>
        ) : (
          <div className="clusters">
            {correlation.softClusters.map((c) => (
              <ClusterRow key={c.id} cluster={c} hardClusters={correlation.clusters} />
            ))}
          </div>
        )}
      </Panel>

      <Panel title="Correlation flags" sub={`${correlation.flags.length}`} flush>
        <FlagList flags={correlation.flags} empty="No correlation concentrations detected." />
      </Panel>
    </>
  );
}

/**
 * One soft cluster, with any hard sub-clusters marked inside it. Tickers that
 * belong to a hard group are chipped together so the "these two are the same
 * position" relationship stays visible inside the looser grouping.
 */
function ClusterRow({ cluster, hardClusters }: { cluster: Cluster; hardClusters: Cluster[] }) {
  const subclusters = cluster.hardSubclusters ?? [];
  const inHard = new Set(subclusters.flat());
  const loose = cluster.tickers.filter((t) => !inHard.has(t));

  const tierLabel =
    cluster.tier === 'soft' ? 'SOFT' : cluster.tier === 'hard' ? 'HARD' : 'STANDALONE';

  // A soft cluster that is exactly one hard cluster is really a hard cluster.
  const singleHard =
    cluster.tier === 'soft' && subclusters.length === 1 && loose.length === 0
      ? hardClusters.find((h) => h.tickers.length === cluster.tickers.length && h.tickers.every((t) => cluster.tickers.includes(t)))
      : undefined;

  return (
    <div className="cluster">
      <div className="cluster__head">
        <span className={`tier tier--${singleHard ? 'hard' : cluster.tier}`}>
          {singleHard ? 'HARD' : tierLabel}
        </span>
        <span className="cluster__label">{cluster.label}</span>
        <span className="cluster__weight num">{pct(cluster.weight)}</span>
      </div>

      <div className="cluster__tickers">
        {subclusters.map((group) => (
          <span className="hardgroup" key={group.join('-')} title="Hard cluster — effectively one position">
            {group.map((t) => (
              <span className="chip chip--hard" key={t}>
                {t}
              </span>
            ))}
          </span>
        ))}
        {loose.map((t) => (
          <span className="chip" key={t}>
            {t}
          </span>
        ))}
      </div>

      <div className="cluster__meta faint">
        {cluster.tier === 'standalone'
          ? 'Standalone position — no peer above the soft threshold.'
          : `${cluster.tickers.length} names, avg pairwise corr ${cluster.avgCorrelation?.toFixed(2) ?? '—'} — ` +
            (singleHard || cluster.tier === 'hard'
              ? 'behaves as one bet.'
              : 'co-moving group; expect these to converge in risk-off.')}
        {subclusters.length > 0 && !singleHard
          ? ` Contains ${subclusters.length} hard sub-cluster${subclusters.length === 1 ? '' : 's'}.`
          : ''}
      </div>
    </div>
  );
}

function Stat({ k, v, note, bad }: { k: string; v: string; note?: string; bad?: boolean }) {
  return (
    <div className="kv__item">
      <div className="kv__k">{k}</div>
      <div className={`kv__v num ${bad ? 'down' : ''}`}>{v}</div>
      {note ? <div className="faint" style={{ fontSize: 10 }}>{note}</div> : null}
    </div>
  );
}
